import { useCallback, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/Button';
import { useOverlayScope } from './context';
import { getOverlayRoot } from './Popover';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export type ModalTone = 'default' | 'danger' | 'accent';

/**
 * 功能性弹窗的强调色。
 * `accent`：蓝色强调，用于「危险但高频」的流程（起飞/降落的条件检查），
 * 保持严肃但不渲染成红色告警。
 */
const MODAL_TONE_CLASSES: Record<ModalTone, string> = {
  default: 'border-line',
  danger: 'border-critical/60',
  accent: 'border-accent/70',
};

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** 强调色：`danger` 红色告警，`accent` 蓝色强调（危险但高频的流程） */
  tone?: ModalTone;
  /** 点击背景是否关闭，默认 true */
  dismissOnBackdrop?: boolean;
  /** 传 false 可隐藏右上角关闭按钮（例如强制修改密码流程） */
  showClose?: boolean;
}

/**
 * 功能性弹窗。
 *
 * 在 `default` 作用域下渲染为居中模态框 + 模糊背景；
 * 在 `realtime` 作用域下（单机管理）**降级为页面内嵌的停靠面板**，
 * 因为硬实时界面不允许出现蒙版遮住遥测与告警。降级由作用域自动完成，
 * 业务代码无法绕过。
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  tone = 'default',
  dismissOnBackdrop = true,
  showClose = true,
}: ModalProps) {
  const scope = useOverlayScope();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // onClose 必须是稳定的引用：调用方普遍写成内联箭头函数
  // （`onClose={() => setCreating(false)}`），每次父组件 render 都会换一个新身份。
  // 下面的 effect 依赖 onClose，所以一旦把它直接写进依赖数组，
  // 用户每敲一个字符 → 受控输入 onChange 触发父组件 render → effect 重跑 →
  // `focusables[0].focus()` 把焦点从输入框拽到右上角关闭按钮（✕）。
  // 用 ref 兜住最新实现，effect 只依赖 open / scope，聚焦逻辑一个打开周期只跑一次。
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open || scope === 'realtime') return;
    restoreFocus.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // 首焦点应落在第一个输入控件上，而不是 DOM 顺序里排在最前面的关闭按钮（✕）：
    // 表单弹窗打开即可直接输入，且不会让用户误触关闭。没有输入控件时才退回第一个可聚焦元素。
    const focusSelector =
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
    const focusables = panel?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const initialTarget =
      (panel?.querySelector<HTMLElement>(focusSelector) as HTMLElement | null | undefined) ??
      focusables?.[0] ??
      panel;
    initialTarget?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key === 'Tab' && panel) {
        const list = panel.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (list.length === 0) return;
        const first = list[0]!;
        const last = list[list.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreFocus.current?.focus?.();
    };
  }, [open, scope]);

  const requestClose = useCallback(() => onClose(), [onClose]);

  // 关闭按钮不吃鼠标聚焦。
  // 纯鼠标的关闭操作如果聚焦了按钮，Chromium 的「空间导航」会在弹窗卸载后
  // 把焦点落到离它最近的按钮上（这里就是弹窗的打开按钮），Safari 则把焦点扔回
  // body；只要页面正好在刷新，焦点就会被那个按钮抢走，表现为输入时焦点乱跳。
  // 用 onMouseDown 阻止默认行为即可彻底避开，键盘用户仍可用 Tab 聚焦。
  const preventMouseFocus = useCallback((event: React.MouseEvent) => event.preventDefault(), []);

  if (!open) return null;

  const body = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={scope === 'default'}
      aria-labelledby={titleId}
      tabIndex={-1}
      className={[
        'flex w-full flex-col overflow-hidden rounded-lg border bg-surface-1 focus:outline-none',
        MODAL_TONE_CLASSES[tone],
        // 高度上限：注册/编辑这类字段很多的功能弹窗在 900px 高的屏幕上会长到 1500px 以上，
        // 不设上限会被 items-center 顶到视口外，页脚按钮既看不到也点不到（实测 top=-304 / bottom=1204）。
        scope === 'realtime' ? '' : `${SIZES[size]} max-h-[calc(100dvh-2rem)]`,
      ].join(' ')}
    >
      <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
        <div className="flex flex-col gap-1">
          <h2 id={titleId} className="text-sm font-semibold text-fg">
            {title}
          </h2>
          {description ? <p className="text-xs leading-relaxed text-fg-muted">{description}</p> : null}
        </div>
        {showClose ? (
          <Button
            size="sm"
            variant="ghost"
            aria-label="关闭"
            onClick={requestClose}
            onMouseDown={preventMouseFocus}
            className="-mt-1 -mr-1"
          >
            ✕
          </Button>
        ) : null}
      </header>

      {children ? (
        // min-h-0 是让 flex-1 真正可以收缩、从而让内部出现滚动条的关键
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5 text-sm text-fg">{children}</div>
      ) : null}

      {footer ? (
        <footer className="flex items-center justify-end gap-2 border-t border-line bg-surface-2 px-4 py-3">
          {footer}
        </footer>
      ) : null}
    </div>
  );

  if (scope === 'realtime') {
    // 实时页：无蒙版、无模糊、不脱离文档流，直接作为页面中的停靠卡片渲染。
    return <div className="w-full">{body}</div>;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/45 backdrop-blur-sm"
        onPointerDown={dismissOnBackdrop ? requestClose : undefined}
      />
      <div className="relative z-10 flex w-full justify-center">{body}</div>
    </div>,
    getOverlayRoot(),
  );
}

export interface PanelProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** 危险/告警面板使用醒目边框 */
  tone?: 'default' | 'danger' | 'warning';
}

const PANEL_TONES: Record<NonNullable<PanelProps['tone']>, string> = {
  default: 'border-line',
  warning: 'border-warning/60',
  danger: 'border-critical/60',
};

/** 常驻卡片面板。实时页的停靠区用它承载命令面板、事件列表等内容。 */
export function Panel({ title, description, actions, children, className = '', tone = 'default' }: PanelProps) {
  return (
    <section
      className={['flex flex-col overflow-hidden rounded-lg border bg-surface-1', PANEL_TONES[tone], className].join(
        ' ',
      )}
    >
      {title || actions ? (
        <header className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            {title ? <h3 className="truncate text-sm font-semibold text-fg">{title}</h3> : null}
            {description ? <p className="truncate text-xs text-fg-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className="flex-1 p-3.5">{children}</div>
    </section>
  );
}
